import type { Edge, Node } from '@xyflow/react'
import type { Graph, GraphConnection, GraphNode } from '@rk/contracts/graph'

/**
 * Перевод между графом сценария и тем, что рисует React Flow.
 *
 * Собственного формата у редактора нет: он правит тот же `Graph`, который
 * хранит служба и проверяет валидатор, — и берёт его тип из того же
 * контракта. Своё описание той же формы разошлось бы с контрактом при
 * первой же правке схемы, и разошлось бы молча (решение задач 17 и 21).
 */

export type { Graph, GraphConnection, GraphNode }

export const EMPTY_GRAPH: Graph = { nodes: [], connections: [] }

/**
 * Данные, которые узел холста несёт с собой.
 *
 * Замечания и исход прогона едут именно здесь, а не берутся из общей
 * таблицы рядом с компонентом: React Flow перерисовывает узел, только когда
 * меняются его данные, — и значение, приходящее в обход них, доезжало бы до
 * экрана лишь вместе со следующей правкой графа. Проверить это на глаз
 * почти нельзя: правка графа обычно и идёт следом.
 */
export interface CanvasNodeData extends Record<string, unknown> {
  node: GraphNode
  /** Сколько замечаний валидатора у узла (FR-UI-001). */
  issues?: number
  /** Чем кончился его шаг в открытом прогоне (FR-ADM-034). */
  outcome?: { status: string; items: number }
}

export function toFlowNodes(graph: Graph): Node<CanvasNodeData>[] {
  return graph.nodes.map((node) => ({
    id: node.id,
    type: 'сценарный',
    position: { x: node.position[0], y: node.position[1] },
    data: { node },
  }))
}

/**
 * Связь графа адресует порт по имени, а React Flow — по идентификатору
 * ручки. Имя порта и есть идентификатор ручки: третьего отображения между
 * ними заводить незачем.
 *
 * `подписьВыхода` называет связь тем выходом, из которого она выходит
 * (FR-ADM-033). У «Условия» два выхода, у «Развилки» пять, и без подписи
 * по графу нельзя сказать, какая ветвь ведёт в 1С, а какая — в дежурный
 * ответ: обе выходят из одного узла и обе — просто линии. Подпись не
 * ставится там, где выход один: «Выход» на каждой связи — это шум, за
 * которым перестают замечать «Истину» и «Ложь».
 */
export function toFlowEdges(
  graph: Graph,
  подписьВыхода?: (node: GraphNode, output: string) => string | undefined,
): Edge[] {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]))

  return graph.connections.map((connection) => {
    const from = byId.get(connection.from.node)
    const label =
      from === undefined || подписьВыхода === undefined
        ? undefined
        : подписьВыхода(from, connection.from.output)

    return {
      id: `${connection.from.node}:${connection.from.output}->${connection.to.node}:${connection.to.input}`,
      source: connection.from.node,
      sourceHandle: connection.from.output,
      target: connection.to.node,
      targetHandle: connection.to.input,
      ...(label === undefined ? {} : { label }),
    }
  })
}

/** Новый узел графа в указанной точке холста. */
export function nodeFromDefinition(
  definition: { type: string; version: number; displayName: string },
  position: { x: number; y: number },
  existing: readonly GraphNode[],
): GraphNode {
  return {
    id: uniqueId(definition.type, existing),
    type: definition.type,
    typeVersion: definition.version,
    name: uniqueName(definition.displayName, existing),
    position: [Math.round(position.x), Math.round(position.y)],
    parameters: {},
    // Значения по умолчанию из схемы выписаны явно: тип контракта требует
    // их, потому что после разбора они всегда есть.
    disabled: false,
    onError: 'stop',
  }
}

/**
 * Имена уникальны в графе, потому что по ним ноды адресуются в выражениях
 * (`$node["Имя"]`). Второй «HTTP-запрос» сделал бы такую ссылку
 * неоднозначной, и публикация была бы отклонена уже валидатором.
 */
function uniqueName(base: string, existing: readonly GraphNode[]): string {
  const taken = new Set(existing.map((node) => node.name))
  if (!taken.has(base)) return base

  for (let index = 2; ; index += 1) {
    const candidate = `${base} ${index}`
    if (!taken.has(candidate)) return candidate
  }
}

function uniqueId(type: string, existing: readonly GraphNode[]): string {
  const taken = new Set(existing.map((node) => node.id))
  for (let index = 1; ; index += 1) {
    const candidate = `${type}-${index}`
    if (!taken.has(candidate)) return candidate
  }
}

/** Добавляет связь, не задваивая уже существующую. */
export function withConnection(graph: Graph, connection: GraphConnection): Graph {
  const already = graph.connections.some(
    (existing) =>
      existing.from.node === connection.from.node &&
      existing.from.output === connection.from.output &&
      existing.to.node === connection.to.node &&
      existing.to.input === connection.to.input,
  )
  if (already) return graph
  return { ...graph, connections: [...graph.connections, connection] }
}

/** Переносит узел, оставляя остальное нетронутым. */
export function withPosition(graph: Graph, nodeId: string, position: { x: number; y: number }): Graph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) =>
      node.id === nodeId
        ? { ...node, position: [Math.round(position.x), Math.round(position.y)] as [number, number] }
        : node,
    ),
  }
}

/**
 * Меняет один параметр узла. `undefined` убирает поле, а не записывает
 * пустоту: узел без параметра берёт значение по умолчанию из описания, а
 * узел с параметром `undefined` — не берёт.
 */
export function withParameter(graph: Graph, nodeId: string, name: string, value: unknown): Graph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (node.id !== nodeId) return node
      const parameters = { ...node.parameters }
      if (value === undefined) delete parameters[name]
      else parameters[name] = value
      return { ...node, parameters }
    }),
  }
}

/**
 * Убирает узел вместе со связями. Связь, ведущая в удалённый узел, — это
 * то, что валидатор отвергнет при публикации, и оставлять её значит
 * заставлять человека чинить за редактором.
 */
export function withoutNode(graph: Graph, nodeId: string): Graph {
  return {
    nodes: graph.nodes.filter((node) => node.id !== nodeId),
    connections: graph.connections.filter(
      (connection) => connection.from.node !== nodeId && connection.to.node !== nodeId,
    ),
  }
}
